import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MinimumFeesApiAdapter } from '../adapters/MinimumFeesApiAdapter';
import { MinimumFeesFilters } from '../models/MinimumFee';

export const minimumFeesQueryKey = ['minimum-fees'];

const DEFAULT_PER_PAGE = 25;

export const useMinimumFeesAdapter = () => useMemo(() => new MinimumFeesApiAdapter(createHttpClient(apiClientWH)), []);

export const useMinimumFees = (filters: Omit<MinimumFeesFilters, 'page' | 'per_page'>) => {
  const adapter = useMinimumFeesAdapter();

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: [...minimumFeesQueryKey, filters],
      queryFn: ({ pageParam = 1 }) =>
        adapter.getMinimumFees({
          ...filters,
          page: pageParam,
          per_page: DEFAULT_PER_PAGE,
        }),
      getNextPageParam: (lastPage) => {
        if (lastPage.meta.page < lastPage.meta.last_page) {
          return lastPage.meta.page + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    });

  // Estabilizar referencia: evita que AgGrid resetee rowData en cada re-render del padre (p. ej. al seleccionar)
  const minimumFees = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const total = data?.pages?.[0]?.meta.total ?? 0;

  return {
    data: minimumFees,
    meta: {
      total,
      per_page: DEFAULT_PER_PAGE,
    },
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};

export const useInvoiceMinimumFees = () => {
  const adapter = useMinimumFeesAdapter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => adapter.invoiceMinimumFees(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: minimumFeesQueryKey });
    },
  });
};
